/**
 * Connection types for studio devices
 *
 * A studio device (`studio_connections` row) has a driver, its
 * `connection_type`. `none` is for devices the bridge does not talk to:
 * passive hardware (a PC, a DeckLink card, a distribution amp) and
 * documentation-only software such as lm-io-server.
 */
export const CONNECTION_TYPES = [
    "videohub",
    "atem",
    "behringer_wing",
    "dlive",
    "companion",
    "vmix",
    "obe_zixi",
    "liveu_server",
    "ultra_encode",
    "generic",
    "none",
];
export const DEVICE_KINDS = ["hardware", "software"];
/** Option keys that hold secrets and must not leave the bridge in responses. */
const SECRET_OPTION_KEY = /pass(word)?|secret|token|api_?key/i;
/** Placeholder returned instead of a set secret. */
export const REDACTED_SECRET = "********";
/**
 * Copy of `options` with every secret value replaced by `REDACTED_SECRET`.
 *
 * The bridge has no authentication, so anything it returns is readable by
 * anyone on the LAN; secrets are only ever written to it, never read back.
 */
export function redactConnectionOptions(options) {
    if (!options)
        return null;
    return Object.fromEntries(Object.entries(options).map(([key, value]) => [
        key,
        SECRET_OPTION_KEY.test(key) && value !== null && value !== ""
            ? REDACTED_SECRET
            : value,
    ]));
}
/**
 * Whether the bridge should run a driver for this device: it needs a driver
 * other than `none` and a host to talk to. Everything else is documentation.
 */
export function hasDeviceDriver(config) {
    return (config.connection_type !== "none" &&
        typeof config.host === "string" &&
        config.host.trim().length > 0);
}
/**
 * Default ports for each connection type (`null`: the type has no network
 * protocol)
 */
export const DEFAULT_PORTS = {
    videohub: 9990,
    atem: 9910,
    behringer_wing: 2222,
    dlive: 51325,
    companion: 8000,
    vmix: 8088,
    obe_zixi: 80,
    liveu_server: 22,
    ultra_encode: 80,
    generic: 80,
    none: null,
};
/**
 * Display names for connection types
 */
export const CONNECTION_TYPE_LABELS = {
    videohub: "Blackmagic VideoHub",
    atem: "ATEM Mixer",
    behringer_wing: "Behringer Wing",
    dlive: "Allen & Heath dLive",
    companion: "Bitfocus Companion",
    vmix: "vMix",
    obe_zixi: "OBE Encoder + Zixi",
    liveu_server: "LiveU Server (SSH)",
    ultra_encode: "Magewell Ultra Encode",
    generic: "Generic Device",
    none: "No driver",
};
/**
 * Connection types whose driver generates its own web-UI links (from host,
 * port and options), so the device shows those instead of a manually entered
 * management URL. The form hides the Management URL field for these.
 */
export const CONNECTION_TYPES_WITH_OWN_LINKS = new Set(["obe_zixi", "ultra_encode"]);
export function hasOwnManagementLinks(type) {
    return CONNECTION_TYPES_WITH_OWN_LINKS.has(type);
}
